import {
  type CommonRequestFn,
  type CommonRequestFnWithoutPayload
} from "@marketplace/http";

import { ENDPOINTS } from "@/constants/endpoints";
import { httpClient } from "@/http";

import type { ApproveBuyerResponseDto } from "../dtos/approve-buyer-response-dto";
import type { CreateSalesRequestDto } from "../dtos/create-sales-request-dto";
import type { CreateSalesResponseDto } from "../dtos/create-sales-response-dto";
import type { CreateSellerRequestDto } from "../dtos/create-seller-request-dto";
import type { CreateSellerResponseDto } from "../dtos/create-seller-response-dto";
import type { DeleteSalesRequestDto } from "../dtos/delete-sales-request-dto";
import type { DeleteSalesResponseDto } from "../dtos/delete-sales-response-dto";
import type { DeleteSellerRequestDto } from "../dtos/delete-seller-request-dto";
import type { DeleteSellerResponseDto } from "../dtos/delete-seller-response-dto";
import type { GetBuyerDetailRequestDto } from "../dtos/get-buyer-detail-request-dto";
import type { GetBuyerDetailResponseDto } from "../dtos/get-buyer-detail-response-dto";
import type { GetBuyerListRequestDto } from "../dtos/get-buyer-list-request-dto";
import type { GetBuyerListResponseDto } from "../dtos/get-buyer-list-response-dto";
import type { GetPicSalesListRequestDto } from "../dtos/get-pic-sales-list-request-dto";
import type { GetPicSalesListResponseDto } from "../dtos/get-pic-sales-list-response-dto";
import type { GetSalesDetailRequestDto } from "../dtos/get-sales-detail-request-dto";
import type { GetSalesDetailResponseDto } from "../dtos/get-sales-detail-response-dto";
import type { GetSalesListRequestDto } from "../dtos/get-sales-list-request-dto";
import type { GetSalesListResponseDto } from "../dtos/get-sales-list-response-dto";
import type { GetSellerDetailRequestDto } from "../dtos/get-seller-detail-request-dto";
import type { GetSellerDetailResponseDto } from "../dtos/get-seller-detail-response-dto";
import type { GetSellerListRequestDto } from "../dtos/get-seller-list-request-dto";
import type { GetSellerListResponseDto } from "../dtos/get-seller-list-response-dto";
import type { GetUserCountResponseDto } from "../dtos/get-user-count-response-dto";
import type { RejectBuyerDocumentRequestDto } from "../dtos/reject-buyer-document-request-dto";
import type { RejectBuyerDocumentResponseDto } from "../dtos/reject-buyer-document-response-dto";
import type { RejectBuyerRequestDto } from "../dtos/reject-buyer-request-dto";
import type { RejectBuyerResponseDto } from "../dtos/reject-buyer-response-dto";
import type { RequestCARequestDto } from "../dtos/request-ca-request-dto";
import type { RequestCAResponseDto } from "../dtos/request-ca-response-dto";
import type { ResendVerificationRequestDto } from "../dtos/resend-verification-request-dto";
import type { ResendVerificationResponseDto } from "../dtos/resend-verification-response-dto";
import type { UpdateSalesRequestDto } from "../dtos/update-sales-request-dto";
import type { UpdateSalesResponseDto } from "../dtos/update-sales-response-dto";
import type { UpdateSellerRequestDto } from "../dtos/update-seller-request-dto";
import type { UpdateSellerResponseDto } from "../dtos/update-seller-response-dto";

type UserServiceContract = {
  createSales: CommonRequestFn<CreateSalesRequestDto, CreateSalesResponseDto>;
  getSalesList: CommonRequestFn<
    GetSalesListRequestDto,
    GetSalesListResponseDto
  >;
  getSalesDetail: CommonRequestFn<
    GetSalesDetailRequestDto,
    GetSalesDetailResponseDto
  >;
  updateSales: CommonRequestFn<UpdateSalesRequestDto, UpdateSalesResponseDto>;
  deleteSales: CommonRequestFn<DeleteSalesRequestDto, DeleteSalesResponseDto>;
  createSeller: CommonRequestFn<
    CreateSellerRequestDto,
    CreateSellerResponseDto
  >;
  getSellerList: CommonRequestFn<
    GetSellerListRequestDto,
    GetSellerListResponseDto
  >;
  getSellerDetail: CommonRequestFn<
    GetSellerDetailRequestDto,
    GetSellerDetailResponseDto
  >;
  updateSeller: CommonRequestFn<
    UpdateSellerRequestDto,
    UpdateSellerResponseDto
  >;
  deleteSeller: CommonRequestFn<
    DeleteSellerRequestDto,
    DeleteSellerResponseDto
  >;
  getBuyerList: CommonRequestFn<
    GetBuyerListRequestDto,
    GetBuyerListResponseDto
  >;
  getBuyerDetail: CommonRequestFn<
    GetBuyerDetailRequestDto,
    GetBuyerDetailResponseDto
  >;
  approveBuyer: CommonRequestFn<
    GetBuyerDetailRequestDto,
    ApproveBuyerResponseDto
  >;
  rejectBuyer: CommonRequestFn<RejectBuyerRequestDto, RejectBuyerResponseDto>;
  rejectBuyerDocument: CommonRequestFn<
    RejectBuyerDocumentRequestDto,
    RejectBuyerDocumentResponseDto
  >;
  getUserCount: CommonRequestFnWithoutPayload<GetUserCountResponseDto>;
  resendVerification: CommonRequestFn<
    ResendVerificationRequestDto,
    ResendVerificationResponseDto
  >;
  requestCA: CommonRequestFn<RequestCARequestDto, RequestCAResponseDto>;
  getPicSalesList: CommonRequestFn<
    GetPicSalesListRequestDto,
    GetPicSalesListResponseDto
  >;
};

export const UserService: UserServiceContract = {
  createSales: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.USERS.SALES.CREATE, {
        json: payload,
        ...options
      })
      .json<CreateSalesResponseDto>(),

  getSalesList: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.USERS.SALES.LIST, {
        searchParams: payload,
        ...options
      })
      .json<GetSalesListResponseDto>(),

  getSalesDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.USERS.SALES.DETAIL(payload.salesId), {
        ...options
      })
      .json<GetSalesDetailResponseDto>(),

  updateSales: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.USERS.SALES.UPDATE(payload.salesId), {
        json: payload,
        ...options
      })
      .json<UpdateSalesResponseDto>(),

  deleteSales: async ({ payload, options }) =>
    httpClient
      .delete(ENDPOINTS.USERS.SALES.DELETE(payload.salesId), {
        ...options
      })
      .json<DeleteSalesResponseDto>(),

  createSeller: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.USERS.SELLER.REGISTER, {
        json: {
          ...payload,
          user_role: "seller-admin"
        },
        ...options
      })
      .json<CreateSellerResponseDto>(),

  getSellerList: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.USERS.SELLER.LIST, {
        searchParams: payload,
        ...options
      })
      .json<GetSellerListResponseDto>(),

  getSellerDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.USERS.SELLER.DETAIL(payload.sellerId), {
        ...options
      })
      .json<GetSellerDetailResponseDto>(),

  updateSeller: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.USERS.SELLER.UPDATE(payload.sellerId), {
        json: payload,
        ...options
      })
      .json<UpdateSellerResponseDto>(),

  deleteSeller: async ({ payload, options }) =>
    httpClient
      .delete(ENDPOINTS.USERS.SELLER.DELETE(payload.sellerId), {
        ...options
      })
      .json<DeleteSellerResponseDto>(),

  getBuyerList: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.USERS.BUYER.LIST, {
        searchParams: payload,
        ...options
      })
      .json<GetBuyerListResponseDto>(),

  getBuyerDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.USERS.BUYER.DETAIL(payload.buyerId), {
        ...options
      })
      .json<GetBuyerDetailResponseDto>(),

  approveBuyer: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.USERS.BUYER.APPROVE(payload.buyerId), {
        json: { company_type: payload.company_type },
        ...options
      })
      .json<ApproveBuyerResponseDto>(),

  rejectBuyer: async ({ payload, options }) =>
    httpClient
      .delete(ENDPOINTS.USERS.BUYER.REJECT_BUYER(payload.buyerId), {
        ...options
      })
      .json<RejectBuyerResponseDto>(),

  getUserCount: async (params) =>
    httpClient
      .get(ENDPOINTS.USERS.COUNT, {
        ...params?.options
      })
      .json<GetUserCountResponseDto>(),

  resendVerification: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.USERS.RESEND_VERIFICATION, {
        json: payload,
        ...options
      })
      .json<ResendVerificationResponseDto>(),

  requestCA: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.USERS.BUYER.REQUEST_CA, {
        json: payload,
        ...options
      })
      .json<RequestCAResponseDto>(),

  getPicSalesList: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.USERS.BUYER.PIC_SALES_LIST(payload.buyerId), {
        ...options
      })
      .json<GetPicSalesListResponseDto>(),

  rejectBuyerDocument: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.USERS.BUYER.REJECT_DOCUMENT(payload.buyerId), {
        json: { verification_comment: payload.verification_comment },
        ...options
      })
      .json<RejectBuyerDocumentResponseDto>()
};
